import React from 'react';
import {
  IAllLanguages,
  ISoftware,
  IProfessionalInfo,
  TimeFormat,
  IProvinces,
  IAllRegions,
  IUtils,
  IContactPreferences,
  TypeOfUser,
  IGeneral,
} from '@pharmaplan/common';
import {
  ArrayToDD,
  dropdownMapper,
  radioButtonMapper,
} from '@pharmaplan/common/helpers/utils';
import { IRatingQuestionValues } from '@pharmaplan/common/models/Models';
import {
  Constants,
  daysDropdown,
  InputTypes,
  ProfileScreens,
  telephoneInputMask,
} from '../../../helpers/Constants';
import ChequeComponent from '../PharmacistFlow/Billing/ChequeComponent';

import strings from '../../../localization';
import style from './style';
import { IPharmacistForm, IPharmacyForm, RadioItemS } from '../types';
import genericClasses from '../../../theme/GenericClasses';
import PharmacyBilling from '../PharmacyFlow/PharmacyBilling';
import { fileUploadTypes } from '../../../hooks/FileUpload/useFileUpload';

const year = new Date().getFullYear();

const startingYear = 1969;
const { uploadFile } = Constants.formInputType;

export const years = Array.from(
  new Array(year - startingYear),
  (val, index) => {
    const value = year - index;

    return { key: value, label: value };
  },
);

export type ICommonContact = typeof commonContact;
export const LanguageLevel = () =>
  [
    {
      key: '0',
      label: strings.weakLevel,
    },
    {
      key: '1',
      label: strings.goodLevel,
    },
    {
      key: '2',
      label: strings.fluentLevel,
    },
  ];

export const languageLevelMap = () =>
  LanguageLevel().map((item) =>
    item.label);

const commonContact = (type: string, requireLabel: boolean) =>
  [
    {
      name: `${type}ContactName`,
      type: Constants.formInputType.text,
      key: 'contactName',
      label: strings.name,
      requireLabel,
      xs: 6,
    },
    {
      name: `${type}ContactPhone`,
      type: Constants.formInputType.masked,
      mask: telephoneInputMask,
      key: 'contactPhone',
      maxLength: 12,
      requireLabel,
      label: strings.phone,
      xs: 6,
    },
    {
      name: `${type}ContactPhoneExt`,
      type: Constants.formInputType.text,
      key: 'contactPhoneExt',
      label: strings.phoneExt,
      maxLength: 6,
      xs: 6,
    },
    {
      name: `${type}ContactMobile`,
      type: Constants.formInputType.masked,
      key: 'contactMobile',
      maxLength: 12,
      label: strings.mobile,
      mask: telephoneInputMask,
      xs: 6,
    },
    {
      name: `${type}ContactEmail`,
      type: Constants.formInputType.text,
      key: 'contactEmail',
      requireLabel,
      label: strings.email,
      xs: 6,
    },
  ];

export const radioButtonConfig = [
  {
    id: TimeFormat.twentyFourHours,
    desc: strings.twentyFourHours,
  },
  {
    id: TimeFormat.twelveHours,
    desc: strings.twelveHours,
  },
];

export const pharmacyField = ({
  languages,
  softwares,
  provinces,
  handleDelete,
  contactPreferences,
  isPharmacyAdmin,
  isSelfService,
}: {
  languages: IAllLanguages[];
  softwares: ISoftware[];
  provinces: IProvinces[];
  isSelfService: boolean;
  handleDelete: () => void;
  setSelected?: React.Dispatch<React.SetStateAction<string>>;
  contactPreferences: IContactPreferences[];
  isPharmacyAdmin: boolean;
}): IPharmacyForm =>
  ({
    pharmacyInfo: [
      {
        hideSave: true,
        form: () =>
          [
            {
              label: strings.banner,
              type: Constants.formInputType.label,
              name: 'logo',
              image: true,
              style: {
                padding: '10px',
              },
              xs: 6,
            },
          ],
      },
      {
        title: strings.general.toUpperCase(),
        hideSave: true,
        form: () =>
          [
            {
              label: strings.loginEmail,
              type: Constants.formInputType.changeEmail,
              xs: 6,
              name: 'email',
              disabled: isPharmacyAdmin,
            },
            {
              label: strings.ownerCompanyName,
              type: Constants.formInputType.text,
              name: 'name',
              xs: 6,
              requireLabel: true,
            },
            {
              label: strings.bannerNumber,
              type: Constants.formInputType.text,
              xs: 6,
              name: 'number',
              requireLabel: true,
            },
            {
              label: strings.software,
              type: Constants.formInputType.dropdown,
              xs: 6,
              name: 'softwareId',
              id: 'softwareId',
              menuItems: dropdownMapper(softwares, 'softwareId', 'name'),
            },
            {
              label: strings.preferredLanguage,
              type: Constants.formInputType.dropdown,
              xs: 6,
              name: 'languageId',
              id: 'languageId',
              menuItems: dropdownMapper(languages, 'languageId', 'name'),
            },
            {
              label: strings.selectedContactPreference,
              type: Constants.formInputType.dropdown,
              requireLabel: true,
              xs: 6,
              multiple: true,
              name: 'selectedContactPreferences',
              id: 'selectedContactPreferences',
              menuItems: dropdownMapper(
                contactPreferences,
                'contactPreferenceId',
                'name',
              ),
            },
            {
              label: strings.monthlyPostingReminder,
              type: Constants.formInputType.checkbox,
              customClass: style.availabilityReminder,
              xs: 6,
              name: 'monthlyPostingReminder',
              id: 'monthlyPostingReminder',
              requireLabel: true,
            },
            {
              type: Constants.formInputType.label,
              xs: 6,
            },
            {
              label: strings.preferredTime,
              type: Constants.formInputType.radio,
              name: 'timeFormat',
              id: 'timeFormat',
              radioItems: radioButtonMapper(radioButtonConfig, 'id', 'desc'),
            },
          ],
      },
      {
        title: strings.weekdays,
        type: 'multiple',
        hideSave: true,
        form: () =>
          [
            {
              label: strings.volume,
              type: Constants.formInputType.text,
              xs: 2,
              requireLabel: true,
              name: 'weekVolume',
              id: 'weekVolume',
            },
            {
              label: strings.pharmacist,
              type: Constants.formInputType.opreation,
              xs: 5,
              requireLabel: true,
              suffix: 'week',
              name: 'Volume',
              opreationType: 'Pharmacists',
              id: 'weekendVolume',
            },
            {
              label: strings.technicians,
              type: Constants.formInputType.opreation,
              requireLabel: true,
              xs: 5,
              opreationType: 'Techs',
              suffix: 'week',
              name: 'Volume',
            },
          ],
      },
      {
        title: strings.weekends,
        type: 'multiple',
        form: () =>
          [
            {
              label: strings.volume,
              type: Constants.formInputType.text,
              xs: 2,
              name: 'weekendVolume',
              requireLabel: true,
              id: 'weekendVolume',
            },
            {
              label: strings.pharmacist,
              type: Constants.formInputType.opreation,
              xs: 5,
              suffix: 'weekend',
              requireLabel: true,
              name: 'Volume',
              opreationType: 'Pharmacists',
            },
            {
              label: strings.technicians,
              type: Constants.formInputType.opreation,
              requireLabel: true,
              xs: 5,
              opreationType: 'Techs',
              suffix: 'weekend',
              name: 'Volume',
            },
            {
              name: 'deleteUser',
              label: strings.deleteMyAccount,
              type: Constants.formInputType.button,
              style: style.deleteButton,
              onClick: handleDelete,
            },
          ],
      },
    ],
    coordinates: [
      {
        title: strings.contacts.toUpperCase(),
        type: 'multiple',
        hideSave: true,
        form: () =>
          [
            {
              key: 'phone',
              type: Constants.formInputType.masked,
              mask: telephoneInputMask,
              name: 'phone',
              label: strings.pharmacyPhone,
              requireLabel: true,
              maxLength: 12,
              xs: 6,
            },
            {
              key: 'fax',
              type: Constants.formInputType.masked,
              mask: telephoneInputMask,
              name: 'fax',
              label: strings.pharmacyFax,
              maxLength: 12,
              xs: 6,
            },
            {
              key: 'emergencyPhone',
              type: Constants.formInputType.masked,
              mask: telephoneInputMask,
              requireLabel: true,
              name: 'emergencyPhone',
              label: strings.emergencyPhone,
              maxLength: 12,
              xs: 6,
            },
          ],
      },
      {
        title: strings.coordinates.toUpperCase(),
        form: () =>
          [
            {
              key: 'address',
              type: Constants.formInputType.text,
              requireLabel: true,
              name: 'address',
              label: strings.address,
              maxLength: 50,
              xs: 6,
            },
            {
              key: 'city',
              type: Constants.formInputType.text,
              name: 'city',
              requireLabel: true,
              label: strings.city,
              xs: 6,
            },
            {
              key: 'postalCode',
              type: Constants.formInputType.text,
              requireLabel: true,
              name: 'postalCode',
              label: strings.postalCode,
              maxLength: 10,
              xs: 6,
            },
            {
              key: 'province',
              type: Constants.formInputType.dropdown,
              name: 'province',
              disabled: true,
              menuItems: dropdownMapper(provinces, 'key', 'name'),
              label: strings.province,
              xs: 6,
            },
            {
              key: 'region',
              type: Constants.formInputType.text,
              name: 'region',
              disabled: true,
              label: strings.region,
              maxLength: 10,
              xs: 6,
            },
          ],
      },
    ],
    primary: {
      title: strings.primary,
      type: 'single',
      form: () =>
        [
          ...commonContact('primary', true),
          {
            key: 'communicationEmail',
            type: Constants.formInputType.text,
            name: 'communicationEmail',
            label: strings.andACopyTo,
            xs: 6,
            requireLabel: false,
          },
        ],
    },
    secondary: {
      title: strings.secondary,
      type: 'single',
      form: () =>
        commonContact('secondary', false),
    },
    accounting: {
      title: strings.accounting,
      type: 'single',
      form: () =>
        commonContact('accounting', true),
    },
    pharmacyBilling: {
      title: 'Hi Pharmacy 1',
      type: 'single',
      form: () =>
        [
          {
            type: Constants.formInputType.component,
            renderComponent: PharmacyBilling,
            xs: 12,
          },
        ],
    },
  });

export const pharmacistFields = ({
  languages,
  proInfoData,
  proUtils,
  regions,
  general,
  isSelfService,
  handleDelete,
  setSelected,
  contactPreferences,
  isFrench,
  userType,
  showGeo,
}: {
  languages: IAllLanguages[];
  proInfoData: IProfessionalInfo;
  proUtils: IUtils;
  general: IGeneral;
  regions: IAllRegions;
  isSelfService: boolean;
  handleDelete: () => void;
  setSelected?: React.Dispatch<React.SetStateAction<string>>;
  contactPreferences: IContactPreferences[];
  isFrench?: boolean;
  userType: TypeOfUser;
  showGeo: () => void;
}): IPharmacistForm => {
  const isStudent = userType === TypeOfUser.student;
  const isTechnician = userType === TypeOfUser.technician;
  const isPharmacist = userType === TypeOfUser.pharmacist;

  return {
    general: [
      {
        title: strings.general.toUpperCase(),
        type: 'multiple',
        hideSave: true,
        form: () =>
          [
            {
              name: 'firstName',
              label: strings.firstName,
              requireLabel: true,
              type: Constants.formInputType.text,
              xs: 6,
            },
            {
              name: 'lastName',
              label: strings.lastName,
              requireLabel: true,
              type: Constants.formInputType.text,
              xs: 6,
            },
            {
              name: 'email',
              label: strings.email,
              type: Constants.formInputType.changeEmail,
              xs: 6,
            },
            {
              xs: 6,
              fileUploadType: fileUploadTypes.resume,
              type: uploadFile,
            },
          ],
      },
      {
        title: strings.preferences.toUpperCase(),
        form: () =>
          [
            {
              label: strings.preferredWorkingDays,
              type: Constants.formInputType.dropdown,
              xs: 6,
              name: 'preferredWorkingDays',
              id: 'preferredWorkingDays',
              multiple: true,
              menuItems: daysDropdown(),
            },
            {
              label: strings.preferredReplacementDuration,
              type: Constants.formInputType.dropdown,
              xs: 6,
              name: 'preferredReplacementDuration',
              id: 'preferredReplacementDuration',
              menuItems: ArrayToDD(general?.prefferedReplacementDurations || []),
            },

            {
              name: 'prefferedDistanceRange',
              label: strings.maxTravelDistance,
              suffix: 'Km',
              type: Constants.formInputType.text,
              xs: 6,
            },
            {
              label: strings.preferredLanguage,
              type: Constants.formInputType.dropdown,
              xs: 6,
              requireLabel: true,
              name: 'preferdLanguage',
              id: 'preferdLanguage',
              menuItems: dropdownMapper(languages, 'languageId', 'name'),
            },
            {
              label: strings.selectedContactPreference,
              type: Constants.formInputType.dropdown,
              requireLabel: true,
              xs: 6,
              multiple: true,
              name: 'selectedContactPreferences',
              id: 'selectedContactPreferences',
              menuItems: dropdownMapper(
                contactPreferences,
                'contactPreferenceId',
                'name',
              ),
            },
            {
              label: strings.preferredTime,
              type: Constants.formInputType.radio,
              name: 'timeFormat',
              id: 'timeFormat',
              horizontal: true,
              radioItems: radioButtonMapper(
                radioButtonConfig,
                'id',
                'desc',
              ) as RadioItemS[],
            },
            {
              label: strings.remoteRegions,
              type: Constants.formInputType.switch,
              name: 'remoteRegion',
              id: 'remoteRegion',
              xs: 6,
              labelClass: genericClasses.formToggle,
            },
            {
              type: Constants.formInputType.label,
              xs: 6,
            },
            {
              label: strings.iAlsoWantToRequestMulti,
              type: Constants.formInputType.switch,
              name: 'includeShiftContract',
              id: 'includeShiftContract',
              xs: 6,
              labelClass: genericClasses.formToggle,
            },
            {
              type: Constants.formInputType.label,
              xs: 6,
            },
            {
              label: strings.notifyMeOnlyIfSelected,
              type: Constants.formInputType.switch,
              name: 'notifyIfSelected',
              id: 'notifyIfSelected',
              xs: isFrench ? 8 : 6,
              labelClass: genericClasses.formToggle,
            },
            {
              type: Constants.formInputType.label,
              xs: isFrench ? 4 : 6,
            },
            {
              label: strings.bookingInvitationEmails,
              type: Constants.formInputType.switch,
              name: 'sendBookingInvitationReminder',
              id: 'sendBookingInvitationReminder',
              xs: isFrench ? 8 : 6,
              labelClass: genericClasses.formToggle,
            },
            {
              type: Constants.formInputType.label,
              xs: isFrench ? 4 : 6,
            },
            {
              name: 'myGeographicAvailability',
              label: strings.myGeographicAvailability,
              xs: 12,
              type: Constants.formInputType.button,
              style: style.alignleft,
              onClick: showGeo,
            },
            {
              type: Constants.formInputType.label,
              xs: 6,
            },
            {
              name: 'deleteUser',
              label: strings.deleteMyAccount,
              type: Constants.formInputType.button,
              style: style.deleteButton,
              onClick: handleDelete,
            },
          ],
      },
    ],
    geographicAvailability: {
      title: strings.myGeographicAvailability.toUpperCase(),
      type: 'single',
      form: () =>
        [
          {
            label: strings.firstName,
            requireLabel: true,
            type: Constants.formInputType.accordion,
            xs: 12,
            items: regions,
          },
        ],
    },
    professionalInfo: [
      {
        title: strings.profissonalInfo.toUpperCase(),
        type: 'multiple',
        form: () =>
          [
            isTechnician && {
              label: strings.diploma,
              infoText: strings.diplomaInfo,
              type: Constants.formInputType.text,
              xs: 6,
              maxLength: 300,
              name: 'diploma',
              id: 'diploma',
              requireLabel: true,
            },
            isTechnician && {
              label: strings.educationalInstitute,
              infoText: strings.educationalInstituteInfo,
              type: Constants.formInputType.text,
              xs: 6,
              maxLength: 300,
              name: 'instituteName',
              id: 'instituteName',
              requireLabel: true,
            },
            isTechnician && {
              label: strings.graduationYear,
              type: Constants.formInputType.dropdown,
              xs: 6,
              name: 'techGraduationYear',
              id: 'techGraduationYear',
              menuItems: years,
              requireLabel: true,
            },
            isPharmacist && {
              label: strings.licenseNo,
              type: Constants.formInputType.text,
              xs: 6,
              name: 'licenseNumber',
              id: 'licenseNumber',
              requireLabel: true,
              maxLength: 8,
            },
            !isStudent && {
              label: isPharmacist
                ? strings.yearLicensed
                : strings.techExperienceSince,
              type: Constants.formInputType.dropdown,
              xs: 6,
              name: 'graduationYear',
              id: 'graduationYear',
              menuItems: years,
              requireLabel: true,
            },
            {
              label: strings.yearsOfExperience,
              xs: 6,
              key: 'yearsOfExperience',
              id: 'yearsOfExperience',
              type: Constants.formInputType.text,
              name: 'yearsOfExperience',
              disabled: true,
            },
            {
              label: strings.frenchLevel,
              type: Constants.formInputType.dropdown,
              xs: 6,
              name: 'frenchLevel',
              id: 'frenchLevel',
              menuItems: LanguageLevel(),
            },
            {
              label: strings.englishLevel,
              type: Constants.formInputType.dropdown,
              xs: 6,
              name: 'englishLevel',
              id: 'englishLevel',
              menuItems: LanguageLevel(),
            },
            {
              label: strings.otherLanguage,
              type: Constants.formInputType.text,
              xs: 6,
              name: 'otherLanguages',
              id: 'otherLanguages',
            },
            {
              label: strings.professionalCapacity,
              type: Constants.formInputType.dropdown,
              xs: 6,
              name: 'pharmacistCapacityId',
              id: 'pharmacistCapacityId',
              menuItems: dropdownMapper(
                proInfoData?.pharmacistCapacities,
                'pharmacistCapacityId',
                'description',
              ),
              requireLabel: true,
            },
            !isSelfService && {
              label: strings.availabilityReminder,
              type: Constants.formInputType.checkbox,
              customClass: style.availabilityReminder,
              xs: 6,
              name: 'avilabilityReminder',
              id: 'avilabilityReminder',
              requireLabel: true,
            },
            isTechnician && {
              type: uploadFile,
              fileUploadType: fileUploadTypes.diploma,
              xs: 6,
            },
            {
              label: strings.areYouIncorporated,
              type: Constants.formInputType.radio,
              xs: 6,
              horizontal: true,
              name: 'incorporated',
              id: 'incorporated',
              radioItems: [
                { label: strings.yes, value: true },
                { label: strings.no, value: false },
              ],
            },
          ],
      },
    ],
    software: {
      title: strings.software.toUpperCase(),
      type: 'table',
      form: () =>
        ({
          options: [
            { key: '0', label: '0%' },
            { key: '1', label: '25%' },
            { key: '2', label: '50%' },
            { key: '3', label: '75%' },
            { key: '4', label: '100%' },
          ],
          rows: proUtils?.softwares,
        }),
    },
    contacts: [
      {
        title: strings.contact.toUpperCase(),
        type: 'multiple',
        hideSave: false,
        form: () =>
          [
            {
              label: strings.address,
              type: Constants.formInputType.text,
              xs: 6,
              maxLength: 50,
              name: 'address',
              id: 'address',
              requireLabel: true,
            },
            {
              label: strings.city,
              type: Constants.formInputType.text,
              xs: 6,
              name: 'city',
              id: 'city',
              requireLabel: true,
            },
            {
              label: strings.postalCode,
              type: Constants.formInputType.text,
              xs: 6,
              name: 'postalCode',
              id: 'postalCode',
              requireLabel: true,
            },
            {
              label: strings.homePhone,
              type: Constants.formInputType.masked,
              mask: telephoneInputMask,
              xs: 6,
              name: 'homePhone',
              id: 'homePhone',
              requireLabel: true,
            },
            {
              label: strings.mobile,
              type: Constants.formInputType.masked,
              mask: telephoneInputMask,
              xs: 6,
              name: 'mobile',
              id: 'mobile',
              requireLabel: true,
            },
            {
              label: strings.province,
              type: Constants.formInputType.text,
              xs: 6,
              disabled: true,
              name: 'province',
              id: 'province',
            },
          ],
      },
    ],
  };
};

export const professionalFields = ({
  languages,
  proInfoData,
  proUtils,
  general,
  regions,
  isSelfService,
  handleDelete,
  setSelected,
  contactPreferences,
  isIncorporated,
  handleEditAccount,
  totalReviews,
  questionValues,
  userType,
  isFrench,
  showGeo,
}: {
  languages: IAllLanguages[];
  proInfoData: IProfessionalInfo;
  general: IGeneral;
  proUtils: IUtils;
  regions: IAllRegions;
  isSelfService: boolean;
  handleDelete: () => void;
  setSelected?: React.Dispatch<React.SetStateAction<string>>;
  contactPreferences: IContactPreferences[];
  isIncorporated: boolean;
  handleEditAccount?: () => void;
  totalReviews?: number;
  questionValues?: Array<IRatingQuestionValues>;
  userType: TypeOfUser;
  isFrench?: boolean;
  showGeo: () => void;
}): IPharmacistForm => {
  const isStudent = userType === TypeOfUser.student;
  const isTechnician = userType === TypeOfUser.technician;
  const isPharmacist = userType === TypeOfUser.pharmacist;

  const incorporatedInfo = [
    {
      label: strings.companyName,
      type: Constants.formInputType.text,
      xs: 6,
      name: 'corporateCompanyName',
      id: 'corporateCompanyName',
      requireLabel: true,
    },
    {
      label: strings.streetAddress,
      type: Constants.formInputType.text,
      xs: 6,
      name: 'corporateStreetAddress',
      maxLength: 50,
      id: 'corporateStreetAddress',
    },
    {
      label: strings.city,
      type: Constants.formInputType.text,
      xs: 6,
      name: 'corporateCity',
      id: 'corporateCity',
    },
    {
      label: strings.province,
      xs: 6,
      name: 'corporateProvince',
      id: 'corporateProvince',
      type: Constants.formInputType.dropdown,
      menuItems: dropdownMapper(proUtils.provinces ?? [], 'key', 'name'),
    },
    {
      type: Constants.formInputType.text,
      xs: 6,
      name: 'corporatePostalCode',
      id: 'corporatePostalCode',
      capitalize: true,
      customType: InputTypes.postalCode,
      label: `${strings.postalCode} (${strings.postalCodeHint})`,
      maxLength: 10,
      placeholder: ' ',
    },
    {
      label: strings.telephoneNumber,
      xs: 6,
      name: 'corporatePhone',
      id: 'corporatePhone',
      type: Constants.formInputType.masked,
      mask: telephoneInputMask,
    },
    {
      label: strings.tpsNumber,
      type: Constants.formInputType.text,
      xs: 6,
      name: 'tps',
      id: 'tps',
      requireLabel: true,
    },
    {
      label: strings.tvqNumber,
      type: Constants.formInputType.text,
      xs: 6,
      name: 'tvq',
      id: 'tvq',
      requireLabel: true,
    },
  ];

  const nonIncorporatedInfo = [
    {
      label: strings.formatString(
        strings.ifApplicableFormat,
        strings.tpsNumber,
      ) as string,
      type: Constants.formInputType.text,
      xs: 6,
      name: 'tps',
      id: 'tps',
    },
    {
      label: strings.formatString(
        strings.ifApplicableFormat,
        strings.tvqNumber,
      ) as string,
      type: Constants.formInputType.text,
      xs: 6,
      name: 'tvq',
      id: 'tvq',
    },
  ];

  let info: any[] = [];
  if (isIncorporated) {
    info = incorporatedInfo;
  } else {
    info = nonIncorporatedInfo;
  }

  return {
    general: [
      {
        title: strings.general.toUpperCase(),
        type: 'multiple',
        hideSave: true,
        form: () =>
          [
            {
              name: 'firstName',
              label: strings.firstName,
              requireLabel: true,
              type: Constants.formInputType.text,
              xs: 6,
            },
            {
              name: 'lastName',
              label: strings.lastName,
              requireLabel: true,
              type: Constants.formInputType.text,
              xs: 6,
            },
            {
              name: 'email',
              label: strings.email,
              type: Constants.formInputType.changeEmail,
              xs: 6,
            },
            {
              xs: 6,
              fileUploadType: fileUploadTypes.resume,
              type: uploadFile,
            },
          ],
      },
      {
        title: strings.preferences.toUpperCase(),
        form: () =>
          [
            {
              label: strings.preferredReplacementDays,
              type: Constants.formInputType.dropdown,
              xs: 6,
              name: 'preferredWorkingDays',
              id: 'preferredWorkingDays',
              multiple: true,
              menuItems: daysDropdown(),
            },
            {
              label: strings.preferredReplacementDuration,
              type: Constants.formInputType.dropdown,
              xs: 6,
              name: 'preferredReplacementDuration',
              id: 'preferredReplacementDuration',
              menuItems: ArrayToDD(general?.prefferedReplacementDurations || []),
            },
            !isSelfService && {
              label: strings.availabilityReminder,
              type: Constants.formInputType.checkbox,
              xs: 6,
              name: 'avilabilityReminder',
              id: 'avilabilityReminder',
              requireLabel: true,
            },
            {
              name: 'prefferedDistanceRange',
              label: strings.maxTravelDistance,
              suffix: 'Km',
              type: Constants.formInputType.text,
              xs: 6,
            },
            {
              label: strings.preferredLanguage,
              type: Constants.formInputType.dropdown,
              xs: 6,
              requireLabel: true,
              name: 'preferdLanguage',
              id: 'preferdLanguage',
              menuItems: dropdownMapper(languages, 'languageId', 'name'),
            },
            {
              label: strings.selectedContactPreference,
              type: Constants.formInputType.dropdown,
              requireLabel: true,
              xs: 6,
              multiple: true,
              name: 'selectedContactPreferences',
              id: 'selectedContactPreferences',
              menuItems: dropdownMapper(
                contactPreferences,
                'contactPreferenceId',
                'name',
              ),
            },

            {
              label: strings.preferredTime,
              type: Constants.formInputType.radio,
              name: 'timeFormat',
              horizontal: true,
              id: 'timeFormat',
              radioItems: radioButtonMapper(
                radioButtonConfig,
                'id',
                'desc',
              ) as RadioItemS[],
            },
            {
              label: strings.remoteRegions,
              type: Constants.formInputType.switch,
              name: 'remoteRegion',
              id: 'remoteRegion',
              xs: 6,
              labelClass: genericClasses.formToggle,
            },
            {
              type: Constants.formInputType.label,
              xs: 6,
            },
            {
              label: strings.iAlsoWantToRequestMulti,
              type: Constants.formInputType.switch,
              name: 'includeShiftContract',
              id: 'includeShiftContract',
              xs: 6,
              labelClass: genericClasses.formToggle,
            },
            {
              type: Constants.formInputType.label,
              xs: 6,
            },
            {
              label: strings.notifyMeOnlyIfSelected,
              type: Constants.formInputType.switch,
              name: 'notifyIfSelected',
              id: 'notifyIfSelected',
              xs: isFrench ? 8 : 6,
              labelClass: genericClasses.formToggle,
            },
            {
              type: Constants.formInputType.label,
              xs: isFrench ? 4 : 6,
            },
            {
              label: strings.bookingInvitationEmails,
              type: Constants.formInputType.switch,
              name: 'sendBookingInvitationReminder',
              id: 'sendBookingInvitationReminder',
              xs: isFrench ? 8 : 6,
              labelClass: genericClasses.formToggle,
            },
            {
              type: Constants.formInputType.label,
              xs: isFrench ? 4 : 6,
            },
            {
              name: 'myGeographicAvailability',
              label: strings.myGeographicAvailability,
              xs: 12,
              type: Constants.formInputType.button,
              style: style.alignleft,
              onClick: showGeo,
            },
            {
              name: 'label',
              type: Constants.formInputType.label,
              style: style.label,
              xs: 6,
            },
            {
              name: 'deleteUser',
              label: strings.deleteMyAccount,
              type: Constants.formInputType.button,
              style: style.deleteButton,
              onClick: handleDelete,
            },
          ],
      },
    ],
    geographicAvailability: {
      title: strings.myGeographicAvailability.toUpperCase(),
      type: 'single',
      form: () =>
        [
          {
            label: strings.firstName,
            requireLabel: true,
            type: Constants.formInputType.accordion,
            xs: 12,
            items: regions,
          },
        ],
    },
    professionalInfo: [
      {
        title: strings.profissonalInfo.toUpperCase(),
        type: 'multiple',
        hideSave: true,
        form: () =>
          [
            isTechnician && {
              label: strings.diploma,
              infoText: strings.diplomaInfo,
              type: Constants.formInputType.text,
              xs: 6,
              maxLength: 300,
              name: 'diploma',
              id: 'diploma',
              requireLabel: true,
            },
            isTechnician && {
              label: strings.educationalInstitute,
              infoText: strings.educationalInstituteInfo,
              type: Constants.formInputType.text,
              xs: 6,
              maxLength: 300,
              name: 'instituteName',
              id: 'instituteName',
              requireLabel: true,
            },
            isTechnician && {
              label: strings.graduationYear,
              type: Constants.formInputType.dropdown,
              xs: 6,
              name: 'techGraduationYear',
              id: 'techGraduationYear',
              menuItems: years,
              requireLabel: true,
            },
            isPharmacist && {
              label: strings.licenseNo,
              type: Constants.formInputType.text,
              xs: 6,
              name: 'licenseNumber',
              id: 'licenseNumber',
              requireLabel: true,
              maxLength: 8,
            },
            !isStudent && {
              label: isPharmacist
                ? strings.yearLicensed
                : strings.techExperienceSince,
              type: Constants.formInputType.dropdown,
              xs: 6,
              name: 'graduationYear',
              id: 'graduationYear',
              menuItems: years,
              requireLabel: true,
            },
            {
              label: strings.frenchLevel,
              type: Constants.formInputType.dropdown,
              xs: 6,
              name: 'frenchLevel',
              id: 'frenchLevel',
              menuItems: LanguageLevel(),
            },
            {
              label: strings.englishLevel,
              type: Constants.formInputType.dropdown,
              xs: 6,
              name: 'englishLevel',
              id: 'englishLevel',
              menuItems: LanguageLevel(),
            },
            {
              label: strings.otherLanguage,
              type: Constants.formInputType.text,
              xs: 6,
              name: 'otherLanguages',
              id: 'otherLanguages',
            },
            {
              label: strings.rxVolumeComfort,
              type: Constants.formInputType.dropdown,
              xs: 6,
              name: 'professionalCapacityId',
              id: 'professionalCapacityId',
              menuItems: dropdownMapper(
                proInfoData?.professionalCapacities,
                'professionalCapacityId',
                'description',
              ),
              requireLabel: true,
            },
            isTechnician && {
              type: uploadFile,
              fileUploadType: fileUploadTypes.diploma,
              xs: 6,
            },
          ],
      },
      {
        title: strings.incorporated.toUpperCase(),
        form: () =>
          [
            {
              label: strings.areYouIncorporated,
              type: Constants.formInputType.radio,
              xs: 12,
              style: style.radio,
              name: 'incorporated',
              id: 'incorporated',
              radioItems: [
                { label: strings.yes, value: true },
                { label: strings.no, value: false },
              ],
            },
            ...info,
          ],
      },
    ],
    [ProfileScreens.professionalProfileAnalytics]: [
      {
        title: strings.profileAnalytics.toUpperCase(),
        hideSave: true,
        type: 'multiple',
        form: () =>
          [
            {
              label: strings.noOfContract,
              type: Constants.formInputType.text,
              xs: 6,
              name: 'confirmedBookings',
              id: 'confirmedBookings',
              disabled: true,
            },
            {
              label: strings.noOfCancellations,
              type: Constants.formInputType.text,
              xs: 6,
              name: 'cancellations',
              id: 'cancellations',
              disabled: true,
            },
          ],
      },
      {
        title: `${strings.punctualityForBookings} (${strings.formatString(
          strings.basedOnNReviews,
          totalReviews ?? 0,
        )})`,
        hideSave: true,
        type: 'multiple',
        form: () =>
          questionValues?.map?.((item) =>
            ({
              name: item.key,
              label: item.value,
              xs: 6,
              disabled: true,
              id: item.key,
              type: Constants.formInputType.text,
            })),
      },
    ],
    software: {
      title: strings.software.toUpperCase(),
      type: 'table',
      form: () =>
        ({
          options: [
            { key: '0', label: '0%' },
            { key: '1', label: '25%' },
            { key: '2', label: '50%' },
            { key: '3', label: '75%' },
            { key: '4', label: '100%' },
          ],
          rows: proUtils?.softwares,
        }),
    },
    contacts: [
      {
        title: strings.contact.toUpperCase(),
        type: 'multiple',
        hideSave: false,
        form: () =>
          [
            {
              label: strings.address,
              type: Constants.formInputType.text,
              xs: 6,
              maxLength: 50,
              name: 'address',
              id: 'address',
              requireLabel: true,
            },
            {
              label: strings.city,
              type: Constants.formInputType.text,
              xs: 6,
              name: 'city',
              id: 'city',
              requireLabel: true,
            },
            {
              label: strings.postalCode,
              type: Constants.formInputType.text,
              xs: 6,
              name: 'postalCode',
              id: 'postalCode',
              requireLabel: true,
            },
            {
              label: strings.homePhone,
              type: Constants.formInputType.masked,
              mask: telephoneInputMask,
              xs: 6,
              name: 'homePhone',
              id: 'homePhone',
              requireLabel: true,
            },
            {
              label: strings.mobile,
              type: Constants.formInputType.masked,
              mask: telephoneInputMask,
              xs: 6,
              name: 'mobile',
              id: 'mobile',
              requireLabel: true,
            },
            {
              label: strings.province,
              type: Constants.formInputType.text,
              xs: 6,
              disabled: true,
              name: 'province',
              id: 'province',
            },
          ],
      },
    ],
    billing: [
      {
        title: strings.sampleCheque,
        type: 'multiple',
        hideSave: true,
        form: () =>
          [
            {
              type: Constants.formInputType.component,
              renderComponent: ChequeComponent,
              xs: 12,
            },
          ],
      },
      {
        title: strings.bankAccountDetails,
        form: () =>
          [
            {
              label: strings.institutionNo,
              type: Constants.formInputType.text,
              xs: 12,
              maxLength: 3,
              name: 'institutionNumber',
              id: 'institutionNumber',
            },
            {
              label: strings.branchNo,
              type: Constants.formInputType.text,
              xs: 12,
              maxLength: 5,
              name: 'branchNumber',
              id: 'branchNumber',
            },
            {
              label: strings.accountNo,
              type: Constants.formInputType.confirmTextField,
              onClick: handleEditAccount,
              xs: 12,
              maxLength: 9,
              name: 'accountNumber',
              id: 'accountNumber',
            },
          ],
      },
    ],
  };
};
